import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { siteEnquiryAPI } from '../utils/api';
import useDebouncedValue from '../hooks/useDebouncedValue';
import '../styles/list.css';
import '../styles/site-enquiries.css';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

const formatDateTime = (value) => {
  if (!value) {
    return { date: '-', time: '' };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { date: '-', time: '' };
  }

  return {
    date: date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    time: date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
};

const SiteEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalEnquiries, setTotalEnquiries] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const normalizedSearch = debouncedSearch.trim();
  const isSearchReady = !normalizedSearch || normalizedSearch.length >= MIN_SEARCH_LENGTH;

  useEffect(() => {
    if (!isSearchReady) {
      return;
    }

    fetchEnquiries();
  }, [debouncedSearch, currentPage, pageSize]);

  const fetchEnquiries = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await siteEnquiryAPI.getAll({
        search: normalizedSearch,
        page: currentPage,
        limit: pageSize,
      });

      setEnquiries(response.data.data.enquiries || []);
      setTotalPages(response.data.data.pages || 1);
      setTotalEnquiries(response.data.data.total || 0);
    } catch (fetchError) {
      setError(fetchError.response?.data?.message || 'Failed to load site enquiries');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const closeModal = () => {
    setSelectedEnquiry(null);
  };

  return (
    <Layout>
      <div className="list-page">
        <div className="list-header">
          <div>
            <h1>Site Enquiry</h1>
            <p className="site-enquiry-subtitle">Enquiries submitted from the public portal are listed here for admins.</p>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="card">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Type at least 3 characters to search by name, phone, email, or message..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setCurrentPage(1);
              }}
            />
            <i className="fas fa-magnifying-glass"></i>
          </div>

          {!isSearchReady && (
            <div className="alert alert-info">Type at least 3 characters to search.</div>
          )}

          {loading ? (
            <div className="spinner"></div>
          ) : enquiries.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table site-enquiry-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone Number</th>
                      <th>Message</th>
                      <th>Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.map((item) => {
                      const submittedAt = formatDateTime(item.created_at);

                      return (
                        <tr key={item.id}>
                          <td>{item.name}</td>
                          <td>{item.phoneNumber}</td>
                          <td className="site-enquiry-message-cell">{item.message || '-'}</td>
                          <td className='width-100'>
                            <div className="site-enquiry-submitted">
                              <strong>{submittedAt.date}</strong>
                              <span>{submittedAt.time}</span>
                            </div>
                          </td>
                          <td className='width-100'>
                            <div className="action-buttons">
                              <button className="btn btn-primary" onClick={() => setSelectedEnquiry(item)}>
                                <i className="fas fa-eye"></i> View
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="fee-pagination-section">
                <div className="pagination-left">
                  <label className="rows-per-page">
                    Rows per page:
                    <select
                      value={pageSize}
                      onChange={(event) => {
                        setPageSize(parseInt(event.target.value, 10));
                        setCurrentPage(1);
                      }}
                      className="page-size-select"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </label>
                  <span className="page-info">Total: {totalEnquiries} enquiries</span>
                </div>

                <div className="pagination-center">
                  <span className="page-indicator">
                    Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                  </span>
                </div>

                <div className="pagination-controls">
                  <button className="btn btn-pagination" onClick={handlePreviousPage} disabled={currentPage === 1}>
                    <i className="fas fa-chevron-left"></i> Previous
                  </button>
                  <button className="btn btn-pagination" onClick={handleNextPage} disabled={currentPage === totalPages}>
                    Next <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="alert alert-info">No site enquiries found yet.</div>
          )}
        </div>

        {selectedEnquiry && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content site-enquiry-modal" onClick={(event) => event.stopPropagation()}>
              <div className="modal-header">
                <h2>Enquiry Details</h2>
                <button className="modal-close" onClick={closeModal} type="button">
                  x
                </button>
              </div>

              <div className="modal-body">
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Name</label>
                    <p>{selectedEnquiry.name}</p>
                  </div>

                  <div className="detail-item">
                    <label>Phone Number</label>
                    <p>{selectedEnquiry.phoneNumber}</p>
                  </div>

                  <div className="detail-item">
                    <label>Email</label>
                    <p>{selectedEnquiry.email || '-'}</p>
                  </div>

                  <div className="detail-item">
                    <label>Submitted On</label>
                    <p>
                      {formatDateTime(selectedEnquiry.created_at).date}
                      {' at '}
                      {formatDateTime(selectedEnquiry.created_at).time || '-'}
                    </p>
                  </div>

                  <div className="detail-item detail-item-wide">
                    <label>Message</label>
                    <p>{selectedEnquiry.message || '-'}</p>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SiteEnquiries;
